import type { Extensions, JSONContent, Editor as TTEditor } from '@tiptap/core';
import type { MentionOptions } from '@tiptap/extension-mention';
import {
  type MouseEvent,
  forwardRef,
  useCallback,
  useRef,
  useMemo,
  createContext,
  useContext,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import { useClickAway } from 'react-use';
import {
  useEditor as useTipTapEditor,
  type UseEditorOptions,
} from '@tiptap/react';
import { TextStyle as TTTextStyle } from '@tiptap/extension-text-style/text-style';
import TTUnderline from '@tiptap/extension-underline';
import TTStrike from '@tiptap/extension-strike';
import TTTextAlign from '@tiptap/extension-text-align';
import TTItalic from '@tiptap/extension-italic';
import TTBold from '@tiptap/extension-bold';
import TTDocument from '@tiptap/extension-document';
import TTParagraph from '@tiptap/extension-paragraph';
import TTText from '@tiptap/extension-text';
import TTHorizontalRule from '@tiptap/extension-horizontal-rule';
import { FontFamily } from './extensions/font-family';
import { FontSize } from './extensions/font-size';
import { Color } from './extensions/color';
import { Highlight } from './extensions/highlight';
import { LineHeight } from './extensions/line-height';
import { Link } from './extensions/link';
import { Mention } from './extensions/mention';
import { DisallowWhitespace } from './extensions/disallow-whitespace';
import { CODE_BLOCK_LANGUAGES, CodeBlock } from './extensions/code-block';
import {
  OrderedList as TTOrderedList,
  ListItem as TTListItem,
  BulletList as TTBulletList,
} from '@tiptap/extension-list';
import { ListKeymap as TTListKeymap } from '@tiptap/extension-list/keymap';
import {
  Placeholder as TTPlaceholder,
  UndoRedo as TTUndoRedo,
} from '@tiptap/extensions';
import Button from '__components/Button';
import { mergeFieldRegex } from 'utility/merge-fields';
import { MergeField } from './MergeFieldNode';
import { useWYSIWYGAIContext } from './ai-context';
import { getMergeFieldSuggestionOptions } from './suggestions/merge-field';
import {
  BottomBarWrapper,
  EditorWrapper,
  FloatingEditorWrapper,
  LoadingContainer,
  ResizeIcon,
  StyledEditorContent,
  StyledResizableBox,
} from './styles';
import type {
  EditorContext,
  WYSIWYGHandle,
  WYSIWYGProps,
  EditorContainerProps,
} from './types';
import { getMergeFieldRelationships } from './utils/merge-fields';
import { handleCopy, handlePaste } from './copy-paste';

const EditorContext = createContext<EditorContext>({
  editor: {} as TTEditor,
});

export const useEditor = () => useContext(EditorContext).editor;

const flattenJsonContent = (node: JSONContent): JSONContent[] => {
  if (!node.content?.length) {
    return [node];
  }

  return node.content.flatMap(flattenJsonContent);
};

export const collectMergeFields = (
  initialValue: Exclude<WYSIWYGProps['initialValue'], undefined>,
  options: { predicate?: (mergeField: string) => boolean } = {}
): string[] => {
  const result: string[] = [];

  if (typeof initialValue === 'string') {
    const matches = initialValue.matchAll(mergeFieldRegex.html());

    for (const match of matches) {
      if (!options.predicate) {
        result.push(match[1]);
      } else if (options.predicate(match[1])) {
        result.push(match[1]);
      }
    }

    return result;
  }

  const leafNodes = flattenJsonContent(initialValue);
  const mergeFieldNodes = leafNodes.filter(
    (node) => node.type === 'mergeField'
  );

  for (const node of mergeFieldNodes) {
    const relationship = node.attrs?.relationship;

    if (relationship) {
      if (!options.predicate) {
        result.push(relationship);
      } else if (options.predicate(relationship)) {
        result.push(relationship);
      }
    }
  }

  return result;
};

const getUnavailableMergeFields = (
  options: Pick<WYSIWYGProps, 'initialValue' | 'mergeFields'>
): string[] => {
  const { initialValue, mergeFields } = options;

  if (!mergeFields || !initialValue) {
    return [];
  }

  const relationships = getMergeFieldRelationships(mergeFields);

  const result = collectMergeFields(initialValue, {
    predicate: (mergeField) => !relationships.has(mergeField),
  });

  return result;
};

const EditorContainer = forwardRef<HTMLDivElement, EditorContainerProps>(
  (
    {
      defaultFontFamily,
      defaultFontSize,
      floating,
      fitToContent,
      showBorder,
      initialHeight,
      minHeight = 100,
      enableResize,
      hasLoadingState = false,
      isLoading = false,
      children,
      codeBlockColors,
    },
    ref
  ) => {
    return floating ? (
      <FloatingEditorWrapper
        ref={ref}
        fitToContent
        defaultFontFamily={defaultFontFamily}
        showBorder={showBorder}
        hasLoadingState={hasLoadingState}
        isLoading={isLoading}
        codeBlockColors={codeBlockColors}
      >
        {children}
      </FloatingEditorWrapper>
    ) : (
      <EditorWrapper
        ref={ref}
        defaultFontFamily={defaultFontFamily}
        defaultFontSize={defaultFontSize}
        fitToContent={fitToContent}
        enableResize={enableResize}
        initialHeight={initialHeight}
        hasLoadingState={hasLoadingState}
        isLoading={isLoading}
        codeBlockColors={codeBlockColors}
      >
        {!enableResize || fitToContent ? (
          children
        ) : (
          <StyledResizableBox
            axis="y"
            resizeHandles={['n', 's']}
            height={initialHeight}
            minConstraints={[
              10,
              Math.min(initialHeight || minHeight, minHeight),
            ]}
            handle={
              <ResizeIcon
                icon="column-resize"
                color="var(--color-background-dark)"
              />
            }
          >
            {children}
          </StyledResizableBox>
        )}
      </EditorWrapper>
    );
  }
);

/*
 * hook to allow an optional callback to never
 * change locally but to ref to the latest
 */
export const usePropCallback = (maybeCallbackProp?: (val: any) => void) => {
  const ref = useRef(maybeCallbackProp);
  ref.current = maybeCallbackProp;
  // This will never change
  return useCallback((val: any) => {
    if (ref.current) {
      ref.current(val);
    }
  }, []);
};

export const WYSIWYG = forwardRef<WYSIWYGHandle, WYSIWYGProps>(
  (
    {
      onInit,
      onChange,
      onFocus,
      onBlur,
      children,

      initialValue = '',
      initialHeight = 200,
      minHeight = 100,

      defaultFontFamily,
      defaultFontSize,
      defaultLineHeight,

      autofocus = false,
      fitToContent = false,
      floating = false,
      showBorder = false,
      zIndex,
      bottomBarNode,

      placeholder,

      enableResize = true,
      enableAI = false,
      enableTextStyles = false,
      enableTextAlign = false,
      enableLists = false,
      enableFontFamily = false,
      enableFontSize = false,
      enableLineHeight = false,
      enableColor = false,
      enableHighlight = false,
      enableLinks = false,
      enableMergeFields = false,
      enableHorizontalRule = false,
      enableDisallowWhitespace = false,
      enableAllObjectMergeFields = false,
      enableMentions = false,
      disabled = false,
      disableCodeBlockEscape = false,
      mentionOptions,
      mergeFields,
      unavailableMergeFields,
      mergeFieldSuggestion,
      codeBlockLanguage,
      preserveWhitespace,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const onChangeCB = usePropCallback(onChange);
    const onFocusCB = usePropCallback(onFocus);
    const onBlurCB = usePropCallback(onBlur);
    const onInitCB = usePropCallback(onInit);
    const {
      isLoading: isLoadingAIContent,
      setShowDismissModal,
      cancelRequest,
    } = useWYSIWYGAIContext();
    const editorConfigDeps = [defaultLineHeight, placeholder, disabled];

    const enableCodeBlock =
      codeBlockLanguage && CODE_BLOCK_LANGUAGES[codeBlockLanguage];

    const editorConfig = useMemo<UseEditorOptions>(() => {
      const notFoundMergeFields =
        unavailableMergeFields ||
        getUnavailableMergeFields({ initialValue, mergeFields });

      const config: UseEditorOptions = {
        onUpdate: onChangeCB,
        onFocus: onFocusCB,
        onBlur: onBlurCB,
        onCreate: onInitCB,
        editorProps: {
          handleDOMEvents: {
            copy: handleCopy,
            cut: handleCopy,
            paste: handlePaste,
          },
        },
        extensions: [
          // Required defaults
          TTDocument,
          TTText,
          preserveWhitespace
            ? TTParagraph.extend({
                parseHTML() {
                  return [
                    {
                      tag: 'p',
                      preserveWhitespace: preserveWhitespace,
                    },
                  ];
                },
              })
            : TTParagraph,
          TTTextStyle,
          TTUndoRedo,

          placeholder && TTPlaceholder.configure({ placeholder }),

          enableFontFamily && FontFamily,
          enableFontSize && FontSize,
          enableLineHeight &&
            LineHeight.configure({ defaultValue: defaultLineHeight }),

          enableTextStyles && TTBold,
          enableTextStyles && TTItalic,
          enableTextStyles && TTUnderline,
          enableTextStyles && TTStrike,
          enableTextStyles && enableColor && Color,
          enableTextStyles && enableHighlight && Highlight,

          enableLists && TTListItem,
          enableLists && TTOrderedList,
          enableLists && TTBulletList,
          enableLists && TTListKeymap,
          enableHorizontalRule && TTHorizontalRule,

          enableTextAlign && TTTextAlign.configure({ types: ['paragraph'] }),

          enableDisallowWhitespace && DisallowWhitespace,

          enableLinks &&
            Link.configure({
              openOnClick: false,
              HTMLAttributes: {
                target: null, // default is _blank which doesn't let us turn it off in the builder - setting null here fixes that.
              },
            }),

          enableMentions && Mention.configure(mentionOptions as MentionOptions),
          enableMergeFields &&
            MergeField.configure({
              mergeFields,
              unavailableMergeFields: notFoundMergeFields,
              suggestion:
                mergeFieldSuggestion ??
                getMergeFieldSuggestionOptions({
                  zIndex,
                  ...(mergeFields
                    ? { props: { mergeFields, listHeaders: false } }
                    : {}),
                }),
            }),
          enableCodeBlock &&
            CodeBlock.configure({
              defaultLanguage: CODE_BLOCK_LANGUAGES[codeBlockLanguage].lang,
              enableToggleCodeBlock: !disableCodeBlockEscape,
              exitOnEmptyDocument: !disableCodeBlockEscape,
              exitOnTripleEnter: !disableCodeBlockEscape,
              exitOnArrowDown: !disableCodeBlockEscape,
              tabContent: codeBlockLanguage === 'python' ? ' '.repeat(4) : '\t',
            }),
        ].filter(Boolean) as Extensions,
        content: initialValue,
        autofocus,
        editable: !disabled,
      };

      return config;
    }, editorConfigDeps); // eslint-disable-line react-hooks/exhaustive-deps

    const editor = useTipTapEditor(editorConfig, editorConfigDeps);
    const editorRef = useRef(editor);
    editorRef.current = editor;

    if (editor) {
      // We're tracking the line height of the last visited paragraph so
      // it can be maintained when there are elements in-between paragraphs
      // (horizontal rule) that we don't want to cause a line height reset.
      const attributes = editor.getAttributes('paragraph');

      if (attributes.lineHeight) {
        editor.extensionStorage.lineHeight.currentLineHeight =
          attributes.lineHeight;
      }
    }

    useImperativeHandle(
      ref,
      () => ({
        editor,
        isEmpty: () => (editor ? editor.getText().length === 0 : true),
        containerRef,
      }),
      [editor, containerRef]
    );

    useClickAway(containerRef, () => {
      if (isLoadingAIContent) {
        setShowDismissModal(true);
      }
    });

    return (
      <EditorContext.Provider value={{ editor }}>
        {children}
        <EditorContainer
          ref={containerRef}
          fitToContent={fitToContent}
          floating={floating}
          showBorder={showBorder}
          enableResize={enableResize}
          initialHeight={initialHeight}
          minHeight={minHeight}
          defaultFontFamily={defaultFontFamily}
          defaultFontSize={defaultFontSize}
          hasLoadingState={enableAI}
          isLoading={isLoadingAIContent}
          codeBlockColors={
            codeBlockLanguage
              ? CODE_BLOCK_LANGUAGES[codeBlockLanguage].colors
              : undefined
          }
        >
          <StyledEditorContent
            editor={editor}
            disabled={disabled}
            enableResize={enableResize}
            initialHeight={initialHeight}
            blurContent={isLoadingAIContent}
          />
          {bottomBarNode && (
            <BottomBarWrapper>{bottomBarNode}</BottomBarWrapper>
          )}
          {isLoadingAIContent && (
            <LoadingContainer className="wysiwyg-loading-container">
              <ClipLoader color="var(--color-background-darker)" />
              <Button
                variant="light"
                color="blue"
                onClick={(ev: MouseEvent<HTMLButtonElement>) => {
                  ev.stopPropagation();
                  cancelRequest();
                }}
              >
                {t('Cancel AI Processing')}
              </Button>
            </LoadingContainer>
          )}
        </EditorContainer>
      </EditorContext.Provider>
    );
  }
);

WYSIWYG.displayName = 'WYSIWYG';
